import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Trash2, Edit, Copy, Printer, Share2, Filter, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import ReactSelect from 'react-select';
const API = process.env.REACT_APP_BACKEND_URL + "/api/procurement";

export default function Quotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Filters state
  const [filters, setFilters] = useState({
    project_id: 'all',
    supplier_name: 'all',
    tag_id: 'all',
    main_category_id: 'all',
    sub_category_id: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const [newQuote, setNewQuote] = useState({
    project_id: '',
    supplier_name: '',
    quote_date: new Date().toISOString().split('T')[0],
    tag_id: '',
    notes: '',
  });

  const [selectedItems, setSelectedItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    item_id: '',
    price: '',
    quantity: ''
  });
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // 'saving', 'saved', ''
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  
  // Quick Add dialogs state
  const [quickAddDialogs, setQuickAddDialogs] = useState({
    project: false,
    supplier: false,
    tag: false,
    item: false
  });
  
  // Quick Add forms state
  const [newProject, setNewProject] = useState({
    project_number: '',
    project_name: '',
    description: '',
    location: ''
  });
  
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    contact_info: ''
  });
  
  const [newTag, setNewTag] = useState({
    name: ''
  });
  
  const [newItem, setNewItem] = useState({
    name: '',
    main_category_id: '',
    sub_category_id: '',
    description: ''
  });

  // Auto-save to localStorage
  useEffect(() => {
    if (open && (newQuote.supplier_name || selectedItems.length > 0)) {
      const saveTimer = setTimeout(() => {
        const draftData = {
          newQuote,
          selectedItems,
          imagePreview,
          editingQuote: editingQuote ? { id: editingQuote.id } : null,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem('quote_draft', JSON.stringify(draftData));
        setAutoSaveStatus('saved');
        setHasUnsavedChanges(true);
        
        // Clear 'saved' status after 2 seconds
        setTimeout(() => setAutoSaveStatus(''), 2000);
      }, 10000); // Auto-save every 10 seconds

      return () => clearTimeout(saveTimer);
    }
  }, [newQuote, selectedItems, open, imagePreview, editingQuote]);

  // Load draft on component mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('quote_draft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        const draftAge = new Date() - new Date(draft.timestamp);
        const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        // Only restore if draft is less than 24 hours old
        if (draftAge < oneDay) {
          toast.info(
            'تم العثور على بيانات محفوظة مسبقاً. هل تريد استرجاعها؟',
            {
              duration: 10000,
              action: {
                label: 'استرجاع',
                onClick: () => {
                  setNewQuote(draft.newQuote);
                  setSelectedItems(draft.selectedItems);
                  setImagePreview(draft.imagePreview);
                  if (draft.editingQuote) {
                    const quote = quotes.find(q => q.id === draft.editingQuote.id);
                    if (quote) setEditingQuote(quote);
                  }
                  setOpen(true);
                  toast.success('تم استرجاع البيانات المحفوظة');
                }
              },
              cancel: {
                label: 'حذف',
                onClick: () => {
                  localStorage.removeItem('quote_draft');
                  toast.success('تم حذف البيانات المحفوظة');
                }
              }
            }
          );
        } else {
          // Remove old draft
          localStorage.removeItem('quote_draft');
        }
      } catch (error) {
        console.error('Error loading draft:', error);
        localStorage.removeItem('quote_draft');
      }
    }
  }, [quotes]);

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && open) {
        e.preventDefault();
        e.returnValue = 'لديك تعديلات غير محفوظة. هل تريد المغادرة؟';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, open]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quotesRes, itemsRes, suppliersRes, projectsRes, tagsRes, mainCatRes, subCatRes] = await Promise.all([
        axios.get(`${API}/quotes`),
        axios.get(`${API}/items`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/tags`),
        axios.get(`${API}/main-categories`),
        axios.get(`${API}/sub-categories`)
      ]);
      setQuotes(quotesRes.data);
      setItems(itemsRes.data);
      setSuppliers(suppliersRes.data);
      setProjects(projectsRes.data);
      setTags(tagsRes.data);
      setMainCategories(mainCatRes.data);
      setSubCategories(subCatRes.data);
    } catch (error) {
      toast.error('خطأ في جلب البيانات');
    }
  };

  // Filter quotes based on selected filters
  const filteredQuotes = quotes.filter(quote => {
    if (filters.project_id && filters.project_id !== 'all' && quote.project_id !== filters.project_id) return false;
    if (filters.supplier_name && filters.supplier_name !== 'all' && quote.supplier_name !== filters.supplier_name) return false;
    if (filters.tag_id && filters.tag_id !== 'all' && quote.tag_id !== filters.tag_id) return false;
    
    // Check if any item in the quote matches the category filters
    if ((filters.main_category_id && filters.main_category_id !== 'all') || (filters.sub_category_id && filters.sub_category_id !== 'all')) {
      const hasMatchingItem = quote.items?.some(item => {
        if (filters.main_category_id && filters.main_category_id !== 'all' && item.main_category_id !== filters.main_category_id) return false;
        if (filters.sub_category_id && filters.sub_category_id !== 'all' && item.sub_category_id !== filters.sub_category_id) return false;
        return true;
      });
      
      // Also check old format
      const oldFormatMatch = (!quote.items || quote.items.length === 0) && (
        (!filters.main_category_id || filters.main_category_id === 'all' || quote.main_category_id === filters.main_category_id) &&
        (!filters.sub_category_id || filters.sub_category_id === 'all' || quote.sub_category_id === filters.sub_category_id)
      );
      
      if (!hasMatchingItem && !oldFormatMatch) return false;
    }
    
    return true;
  });

  const clearFilters = () => {
    setFilters({
      project_id: 'all',
      supplier_name: 'all',
      tag_id: 'all',
      main_category_id: 'all',
      sub_category_id: 'all'
    });
  };

  // Get unique suppliers from quotes
  const uniqueSuppliers = [...new Set(quotes.map(q => q.supplier_name))].filter(Boolean);

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {'image/*': []},
    multiple: false
  });

  const handleItemSelect = (itemId) => {
    setCurrentItem({
      ...currentItem,
      item_id: itemId
    });
  };

  const handleAddItem = () => {
    if (!currentItem.item_id || !currentItem.price || !currentItem.quantity) {
      toast.error('الرجاء إدخال الصنف والسعر والكمية');
      return;
    }

    const selectedItemData = items.find(i => i.id === currentItem.item_id);
    if (!selectedItemData) return;

    const newItem = {
      item_id: selectedItemData.id,
      item_name: selectedItemData.name,
      main_category_id: selectedItemData.main_category_id || selectedItemData.category_id,
      main_category_name: selectedItemData.main_category_name || selectedItemData.category_name,
      sub_category_id: selectedItemData.sub_category_id,
      sub_category_name: selectedItemData.sub_category_name,
      price: parseFloat(currentItem.price),
      quantity: parseFloat(currentItem.quantity)
    };

    setSelectedItems([...selectedItems, newItem]);
    setCurrentItem({ item_id: '', price: '', quantity: '' });
    toast.success('تم إضافة الصنف');
  };

  const handleRemoveItem = (index) => {
    const newItems = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(newItems);
    toast.success('تم حذف الصنف');
  };

  const handleUpdateItem = (index, field, value) => {
    const newItems = [...selectedItems];
    newItems[index][field] = value === '' ? '' : parseFloat(value) || value;
    setSelectedItems(newItems);
  };

  const handleAdd = async () => {
    if (!newQuote.supplier_name || selectedItems.length === 0) {
      toast.error('الرجاء اختيار المورد وإضافة صنف واحد على الأقل');
      return;
    }

    setLoading(true);
    try {
      let imageUrl = editingQuote?.image_url || '';
      
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadRes = await axios.post(`${API}/quotes/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000 // 60 seconds for image upload
        });
        imageUrl = uploadRes.data.image_url;
      }

      const selectedProject = projects.find(p => p.id === newQuote.project_id);
      
      const quoteData = {
        project_id: newQuote.project_id || null,
        project_number: selectedProject?.project_number || null,
        project_name: selectedProject?.project_name || null,
        supplier_name: newQuote.supplier_name,
        items: selectedItems,
        quote_date: newQuote.quote_date,
        tag_id: newQuote.tag_id || null,
        tag_name: newQuote.tag_name || null,
        notes: newQuote.notes,
        image_url: imageUrl
      };

      // Show progress toast for large quotes
      const itemCount = selectedItems.length;
      if (itemCount > 20) {
        toast.info(`جاري حفظ عرض السعر (${itemCount} صنف)... الرجاء الانتظار`, {
          duration: 5000
        });
      }

      if (editingQuote) {
        // Update existing quote with extended timeout
        await axios.put(`${API}/quotes/${editingQuote.id}`, quoteData, {
          timeout: 120000 // 120 seconds (2 minutes) for large quotes
        });
        toast.success('تم تحديث عرض السعر بنجاح');
      } else {
        // Create new quote with extended timeout
        await axios.post(`${API}/quotes`, quoteData, {
          timeout: 120000 // 120 seconds (2 minutes) for large quotes
        });
        toast.success('تم إضافة عرض السعر بنجاح');
      }
      
      setNewQuote({
        project_id: '',
        supplier_name: '',
        quote_date: new Date().toISOString().split('T')[0],
        tag_id: '',
        notes: '',
      });
      setSelectedItems([]);
      setCurrentItem({ item_id: '', price: '', quantity: '' });
      setImageFile(null);
      setImagePreview(null);
      setEditingQuote(null);
      setHasUnsavedChanges(false);
      setOpen(false);
      
      // Clear auto-saved draft after successful save
      localStorage.removeItem('quote_draft');
      
      fetchData();
    } catch (error) {
      console.error('Error saving quote:', error);
      
      // More detailed error messages
      let errorMessage = editingQuote ? 'خطأ في تحديث عرض السعر' : 'خطأ في إضافة عرض السعر';
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage = 'انتهت مهلة الحفظ. الرجاء المحاولة مرة أخرى. إذا كان عرض السعر كبيراً، قد يستغرق وقتاً أطول.';
      } else if (error.response) {
        // Server responded with error
        const detail = error.response.data?.detail;
        
        if (Array.isArray(detail)) {
          // If detail is an array of validation errors, format them nicely
          const errorMessages = detail.map((err, idx) => {
            if (typeof err === 'object') {
              return `${idx + 1}. ${err.msg || err.message || JSON.stringify(err)}`;
            }
            return `${idx + 1}. ${err}`;
          }).join('\n');
          errorMessage = `خطأ في التحقق من البيانات:\n${errorMessages}`;
        } else if (typeof detail === 'string') {
          errorMessage = `خطأ: ${detail}`;
        } else if (typeof detail === 'object') {
          errorMessage = `خطأ: ${JSON.stringify(detail)}`;
        } else {
          errorMessage = `خطأ: ${error.response.statusText}`;
        }
      } else if (error.request) {
        // Request was made but no response
        errorMessage = 'لم يتم استلام رد من الخادم. تأكد من اتصال الإنترنت.';
      }
      
      // For long error messages, show only first part
      if (errorMessage.length > 200) {
        errorMessage = errorMessage.substring(0, 200) + '...';
      }
      
      toast.error(errorMessage, { duration: 7000 });
    }
    setLoading(false);
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setNewQuote({
      project_id: quote.project_id || '',
      supplier_name: quote.supplier_name,
      quote_date: quote.quote_date,
      tag_id: quote.tag_id || '',
      notes: quote.notes || '',
    });
    
    // Load items
    if (quote.items && quote.items.length > 0) {
      setSelectedItems(quote.items);
    } else {
      // Old format
      setSelectedItems([{
        item_id: quote.item_id,
        item_name: quote.item_name,
        main_category_id: quote.main_category_id,
        main_category_name: quote.main_category_name,
        sub_category_id: quote.sub_category_id,
        sub_category_name: quote.sub_category_name,
        price: quote.price,
        quantity: quote.quantity
      }]);
    }
    
    // Load image preview if exists
    if (quote.image_url) {
      setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${quote.image_url}`);
    }
    
    setOpen(true);
  };

  // Quick Add Functions
  const handleQuickAddProject = async () => {
    if (!newProject.project_number || !newProject.project_name) {
      toast.error('يرجى إدخال رقم واسم المشروع');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/projects`, newProject);
      setProjects([...projects, response.data]);
      setNewQuote({...newQuote, project_id: response.data.id});
      setQuickAddDialogs({...quickAddDialogs, project: false});
      setNewProject({ project_number: '', project_name: '', description: '', location: '' });
      toast.success('تم إضافة المشروع بنجاح');
    } catch (error) {
      console.error('Error adding project:', error);
      toast.error('خطأ في إضافة المشروع');
    }
  };
  
  const handleQuickAddSupplier = async () => {
    if (!newSupplier.name) {
      toast.error('يرجى إدخال اسم المورد');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/suppliers`, newSupplier);
      setSuppliers([...suppliers, response.data]);
      setNewQuote({...newQuote, supplier_name: response.data.name});
      setQuickAddDialogs({...quickAddDialogs, supplier: false});
      setNewSupplier({ name: '', contact_info: '' });
      toast.success('تم إضافة المورد بنجاح');
    } catch (error) {
      console.error('Error adding supplier:', error);
      toast.error('خطأ في إضافة المورد');
    }
  };
  
  const handleQuickAddTag = async () => {
    if (!newTag.name) {
      toast.error('يرجى إدخال اسم التصنيف');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tags`, newTag);
      setTags([...tags, response.data]);
      setNewQuote({...newQuote, tag_id: response.data.id});
      setQuickAddDialogs({...quickAddDialogs, tag: false});
      setNewTag({ name: '' });
      toast.success('تم إضافة التصنيف بنجاح');
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('خطأ في إضافة التصنيف');
    }
  };
  
  const handleQuickAddItem = async () => {
    if (!newItem.name || !newItem.main_category_id || !newItem.sub_category_id) {
      toast.error('يرجى إدخال اسم الصنف والتصنيف الرئيسي والفرعي');
      return;
    }
    
    try {
      const mainCat = mainCategories.find(c => c.id === newItem.main_category_id);
      const subCat = subCategories.find(c => c.id === newItem.sub_category_id);
      
      const itemData = {
        ...newItem,
        main_category_name: mainCat?.name || '',
        sub_category_name: subCat?.name || ''
      };
      
      const response = await axios.post(`${API}/items`, itemData);
      setItems([...items, response.data]);
      setCurrentItem({...currentItem, item_id: response.data.id});
      setQuickAddDialogs({...quickAddDialogs, item: false});
      setNewItem({ name: '', main_category_id: '', sub_category_id: '', description: '' });
      toast.success('تم إضافة الصنف بنجاح');
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('خطأ في إضافة الصنف');
    }
  };

  const handleDuplicate = (quote) => {
    // Copy all data except prices, supplier, and image
    setEditingQuote(null); // This is a new quote, not editing
    setNewQuote({
      project_id: quote.project_id || '',
      supplier_name: '', // Empty - user will select new supplier
      quote_date: quote.quote_date,
      tag_id: quote.tag_id || '',
      notes: quote.notes || '',
    });
    
    // Load items with quantities but WITHOUT prices
    if (quote.items && quote.items.length > 0) {
      setSelectedItems(quote.items.map(item => ({
        ...item,
        price: '' // Empty price - user will enter new prices
      })));
    } else {
      // Old format
      setSelectedItems([{
        item_id: quote.item_id,
        item_name: quote.item_name,
        main_category_id: quote.main_category_id,
        main_category_name: quote.main_category_name,
        sub_category_id: quote.sub_category_id,
        sub_category_name: quote.sub_category_name,
        price: '', // Empty price
        quantity: quote.quantity
      }]);
    }
    
    // Don't load image
    setImageFile(null);
    setImagePreview(null);
    
    setOpen(true);
    toast.success('تم نسخ الأصناف! اختر المورد الجديد وأدخل الأسعار');
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/quotes/${id}`);
      toast.success('تم حذف عرض السعر بنجاح');
      fetchData();
    } catch (error) {
      toast.error('خطأ في حذف عرض السعر');
    }
  };

  const handlePrint = (quote) => {
    // Create a printable content
    const printWindow = window.open('', '_blank');
    const project = projects.find(p => p.id === quote.project_id);
    
    let itemsHTML = '';
    if (quote.items && quote.items.length > 0) {
      itemsHTML = quote.items.map(item => `
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">${item.item_name}</td>
          <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${item.price} ريال</td>
          <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${item.quantity}</td>
          <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${(item.price * item.quantity).toFixed(2)} ريال</td>
        </tr>
      `).join('');
    }
    
    const totalAmount = quote.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <title>عرض السعر - ${quote.supplier_name}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            direction: rtl;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
          }
          .company-name {
            font-size: 28px;
            font-weight: bold;
            color: #1e40af;
            margin-bottom: 10px;
          }
          .document-title {
            font-size: 24px;
            color: #374151;
            margin-top: 10px;
          }
          .info-section {
            margin-bottom: 20px;
            background: #f9fafb;
            padding: 15px;
            border-radius: 8px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            padding: 5px 0;
          }
          .info-label {
            font-weight: bold;
            color: #374151;
          }
          .info-value {
            color: #6b7280;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th {
            background-color: #2563eb;
            color: white;
            padding: 12px;
            text-align: right;
            border: 1px solid #1e40af;
          }
          td {
            border: 1px solid #ddd;
            padding: 8px;
          }
          .total-row {
            background-color: #dbeafe;
            font-weight: bold;
            font-size: 16px;
          }
          .summary-box {
            margin-top: 20px;
            display: flex;
            justify-content: flex-start;
          }
          .summary-table {
            width: 380px;
            border-collapse: collapse;
            border: 2px solid #1e40af;
            border-radius: 6px;
            overflow: hidden;
          }
          .summary-table td {
            padding: 10px 14px;
            border-bottom: 1px solid #e5e7eb;
            font-size: 14px;
          }
          .summary-table tr:last-child td { border-bottom: none; }
          .summary-table td.label {
            background: #f9fafb;
            font-weight: bold;
            color: #374151;
            width: 55%;
          }
          .summary-table td.value {
            text-align: left;
            color: #111827;
            font-weight: 600;
          }
          .summary-table tr.grand td {
            background: #1e40af;
            color: white;
            font-size: 16px;
            font-weight: bold;
          }
          .summary-table tr.tax td.value { color: #d97706; }
          .footer {
            margin-top: 40px;
            text-align: center;
            color: #6b7280;
            font-size: 12px;
            border-top: 1px solid #e5e7eb;
            padding-top: 20px;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">شركة الغزالي</div>
          <div class="document-title">عرض السعر</div>
        </div>
        
        <div class="info-section">
          <div class="info-row">
            <span class="info-label">المورد:</span>
            <span class="info-value">${quote.supplier_name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">المشروع:</span>
            <span class="info-value">${project ? `${project.project_number} - ${project.project_name}` : '-'}</span>
          </div>
          <div class="info-row">
            <span class="info-label">التاريخ:</span>
            <span class="info-value">${quote.quote_date}</span>
          </div>
          ${quote.tag_name ? `
          <div class="info-row">
            <span class="info-label">التصنيف:</span>
            <span class="info-value">${quote.tag_name}</span>
          </div>
          ` : ''}
          ${quote.notes ? `
          <div class="info-row">
            <span class="info-label">ملاحظات:</span>
            <span class="info-value">${quote.notes}</span>
          </div>
          ` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th>الصنف</th>
              <th style="text-align: center;">السعر</th>
              <th style="text-align: center;">الكمية</th>
              <th style="text-align: center;">الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>

        <div class="summary-box">
          <table class="summary-table">
            <tr>
              <td class="label">المجموع (قبل الضريبة):</td>
              <td class="value">${totalAmount.toFixed(2)} ريال</td>
            </tr>
            <tr class="tax">
              <td class="label">ضريبة القيمة المضافة (15%):</td>
              <td class="value">${(totalAmount * 0.15).toFixed(2)} ريال</td>
            </tr>
            <tr class="grand">
              <td class="label">الإجمالي الكلي (شامل الضريبة):</td>
              <td class="value">${(totalAmount * 1.15).toFixed(2)} ريال</td>
            </tr>
          </table>
        </div>

        <div class="footer">
          <p>شركة الغزالي - نظام إدارة المشتريات</p>
          <p>تاريخ الطباعة: ${new Date().toLocaleDateString('ar-SA')}</p>
        </div>

        <div class="no-print" style="margin-top: 20px; text-align: center;">
          <button onclick="window.print()" style="padding: 10px 20px; background: #2563eb; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 16px;">
            طباعة
          </button>
          <button onclick="window.close()" style="padding: 10px 20px; background: #6b7280; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 16px; margin-right: 10px;">
            إغلاق
          </button>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleShare = async (quote) => {
    const project = projects.find(p => p.id === quote.project_id);
    const itemsList = quote.items?.map(item => 
      `${item.item_name}: ${item.price} ريال × ${item.quantity} = ${(item.price * item.quantity).toFixed(2)} ريال`
    ).join('\n') || '';
    
    const totalAmount = quote.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
    
    const shareText = `عرض السعر - شركة الغزالي

المورد: ${quote.supplier_name}
المشروع: ${project ? `${project.project_number} - ${project.project_name}` : '-'}
التاريخ: ${quote.quote_date}
${quote.tag_name ? `التصنيف: ${quote.tag_name}\n` : ''}

الأصناف:
${itemsList}

المجموع (قبل الضريبة): ${totalAmount.toFixed(2)} ريال
ضريبة القيمة المضافة (15%): ${(totalAmount * 0.15).toFixed(2)} ريال
الإجمالي الكلي (شامل الضريبة): ${(totalAmount * 1.15).toFixed(2)} ريال

${quote.notes ? `\nملاحظات: ${quote.notes}` : ''}`;

    // Try to use Web Share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: `عرض السعر - ${quote.supplier_name}`,
          text: shareText
        });
        toast.success('تمت المشاركة بنجاح');
      } catch (error) {
        // User cancelled or error occurred
        if (error.name !== 'AbortError') {
          copyToClipboard(shareText);
        }
      }
    } else {
      // Fallback to clipboard
      copyToClipboard(shareText);
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        toast.success('تم نسخ تفاصيل العرض! يمكنك مشاركتها الآن');
      }).catch(() => {
        toast.error('خطأ في النسخ');
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('تم نسخ تفاصيل العرض! يمكنك مشاركتها الآن');
      } catch (err) {
        toast.error('خطأ في النسخ');
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 p-3 md:p-6">
      <div className="container mx-auto max-w-6xl">
        {/* Back button - Mobile priority */}
        <div className="mb-4">
          <Button 
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/procurement')} 
            variant="outline"
            className="gap-2 min-w-[120px] w-full md:w-auto md:hidden"
          >
            <ArrowLeft className="w-5 h-5 shrink-0" />
            <span>العودة للرئيسية</span>
          </Button>
        </div>
        
        <div className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-800 mb-2" style={{fontFamily: 'Manrope'}}>
              عروض الأسعار
            </h1>
            <p className="text-sm md:text-base text-slate-600">إدارة عروض الأسعار من الموردين</p>
          </div>
          <div className="flex gap-2 md:gap-3 items-center flex-wrap">
            <Button
              variant="outline"
              onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
              className="flex items-center gap-2 bg-white border-2 border-purple-500 text-purple-600 hover:bg-purple-50"
              title={viewMode === 'table' ? 'عرض البطاقات' : 'عرض الجدول'}
            >
              {viewMode === 'table' ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  <span className="hidden sm:inline">بطاقات</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="hidden sm:inline">جدول</span>
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 bg-white border-2 border-blue-500 text-blue-600 hover:bg-blue-50"
              data-testid="toggle-filters-btn"
            >
              {showFilters ? <X className="w-4 h-4" /> : <Filter className="w-4 h-4" />}
              <span className="hidden sm:inline">{showFilters ? 'إخفاء الفلاتر' : 'إظهار الفلاتر'}</span>
              {!showFilters && (
                <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                  {filteredQuotes.length}/{quotes.length}
                </span>
              )}
            </Button>
            
            <Dialog open={open} onOpenChange={(isOpen) => {
              // When closing dialog, check for unsaved changes
              if (!isOpen && hasUnsavedChanges) {
                const confirmClose = window.confirm('لديك تعديلات محفوظة تلقائياً. هل تريد الإغلاق؟ (يمكنك استرجاع البيانات لاحقاً)');
                if (!confirmClose) {
                  return; // Don't close
                }
              }
              
              setOpen(isOpen);
              if (!isOpen) {
                setEditingQuote(null);
                setNewQuote({
                  project_id: '',
                  supplier_name: '',
                  quote_date: new Date().toISOString().split('T')[0],
                  tag_id: '',
                  notes: '',
                });
                setSelectedItems([]);
                setImageFile(null);
                setImagePreview(null);
                setAutoSaveStatus('');
              }
            }}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="add-quote-btn"
                  className="bg-gradient-to-r from-purple-500 to-purple-600 hover:shadow-lg gap-2"
                >
                  <Plus className="w-4 h-4" />
                  إضافة عرض سعر
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <DialogTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>
                        {editingQuote ? 'تعديل عرض السعر' : 'إضافة عرض سعر جديد'}
                      </DialogTitle>
                      <DialogDescription>
                        {editingQuote ? 'قم بتعديل تفاصيل عرض السعر' : 'أدخل تفاصيل عرض السعر من المورد'}
                      </DialogDescription>
                    </div>
                    {autoSaveStatus === 'saved' && (
                      <div className="flex items-center gap-2 text-green-600 text-sm">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>تم الحفظ تلقائياً</span>
                      </div>
                    )}
                  </div>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>المشروع (اختياري)</Label>
                    <div className="flex gap-2 mt-2">
                      {/* Native select for mobile */}
                      <select
                        data-testid="project-select"
                        value={newQuote.project_id}
                        onChange={(e) => setNewQuote({...newQuote, project_id: e.target.value})}
                        className="flex-1 md:hidden h-12 px-3 py-2 text-sm rounded-md border border-input bg-background ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        style={{fontSize: '16px'}}
                      >
                        <option value="">اختر المشروع</option>
                        {projects.map(project => (
                          <option key={project.id} value={project.id}>
                            {project.project_number} - {project.project_name}
                          </option>
                        ))}
                      </select>
                      
                      {/* Shadcn Select for desktop */}
                      <Select 
                        value={newQuote.project_id} 
                        onValueChange={(value) => setNewQuote({...newQuote, project_id: value})}
                      >
                        <SelectTrigger data-testid="project-select-desktop" className="flex-1 hidden md:flex">
                          <SelectValue placeholder="اختر المشروع" />
                        </SelectTrigger>
                        <SelectContent>
                          {projects.map(project => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.project_number} - {project.project_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setQuickAddDialogs({...quickAddDialogs, project: true})}
                        className="shrink-0"
                        title="إضافة مشروع جديد"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label>المورد</Label>
                    <div className="flex gap-2 mt-2">
                      {/* Native select for mobile, Shadcn for desktop */}
                      <select
                        data-testid="supplier-select"
                        value={newQuote.supplier_name}
                        onChange={(e) => setNewQuote({...newQuote, supplier_name: e.target.value})}
                        className="flex-1 md:hidden h-12 px-3 py-2 text-sm rounded-md border border-input bg-background ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        style={{fontSize: '16px'}}
                      >
                        <option value="">اختر المورد</option>
                        {suppliers.map(supplier => (
                          <option key={supplier.id} value={supplier.name}>{supplier.name}</option>
                        ))}
                      </select>
                      
                      {/* Shadcn Select for desktop */}
                      <Select 
                        value={newQuote.supplier_name} 
                        onValueChange={(value) => setNewQuote({...newQuote, supplier_name: value})}
                      >
                        <SelectTrigger data-testid="supplier-select-desktop" className="flex-1 hidden md:flex">
                          <SelectValue placeholder="اختر المورد" />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map(supplier => (
                            <SelectItem key={supplier.id} value={supplier.name}>{supplier.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setQuickAddDialogs({...quickAddDialogs, supplier: true})}
                        className="shrink-0"
                        title="إضافة مورد جديد"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label>تاريخ العرض</Label>
                    <Input
                      data-testid="quote-date-input"
                      type="date"
                      value={newQuote.quote_date}
                      onChange={(e) => setNewQuote({...newQuote, quote_date: e.target.value})}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label>تصنيف العرض (اختياري)</Label>
                    <div className="flex gap-2 mt-2">
                      {/* Native select for mobile */}
                      <select
                        data-testid="tag-select"
                        value={newQuote.tag_id}
                        onChange={(e) => {
                          const selectedTag = tags.find(t => t.id === e.target.value);
                          setNewQuote({
                            ...newQuote,
                            tag_id: e.target.value,
                            tag_name: selectedTag ? selectedTag.name : ''
                          });
                        }}
                        className="flex-1 md:hidden h-12 px-3 py-2 text-sm rounded-md border border-input bg-background ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        style={{fontSize: '16px'}}
                      >
                        <option value="">اختر التصنيف</option>
                        {tags.map(tag => (
                          <option key={tag.id} value={tag.id}>{tag.name}</option>
                        ))}
                      </select>
                      
                      {/* Shadcn Select for desktop */}
                      <Select 
                        value={newQuote.tag_id} 
                        onValueChange={(value) => {
                          const selectedTag = tags.find(t => t.id === value);
                          setNewQuote({
                            ...newQuote, 
                            tag_id: value,
                            tag_name: selectedTag ? selectedTag.name : ''
                          });
                        }}
                      >
                        <SelectTrigger data-testid="tag-select-desktop" className="flex-1 hidden md:flex">
                          <SelectValue placeholder="اختر التصنيف" />
                        </SelectTrigger>
                        <SelectContent>
                          {tags.map(tag => (
                            <SelectItem key={tag.id} value={tag.id}>{tag.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setQuickAddDialogs({...quickAddDialogs, tag: true})}
                        className="shrink-0"
                        title="إضافة تصنيف جديد"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-lg font-semibold">الأصناف</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setQuickAddDialogs({...quickAddDialogs, item: true})}
                        className="gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="hidden sm:inline">إضافة صنف جديد</span>
                      </Button>
                    </div>
                    
                    <div className="mt-3 space-y-3">
                      <div>
                        <Label className="text-sm">اختر الصنف (يمكنك البحث)</Label>
                        <ReactSelect
                          options={items.map(item => ({
                            value: item.id,
                            label: `${item.name} - (${item.main_category_name || item.category_name})`,
                            item: item
                          }))}
                          value={currentItem.item_id ? {
                            value: currentItem.item_id,
                            label: items.find(i => i.id === currentItem.item_id)?.name || ''
                          } : null}
                          onChange={(selected) => handleItemSelect(selected?.value || '')}
                          placeholder="ابحث عن الصنف..."
                          isClearable
                          isSearchable
                          noOptionsMessage={() => "لا توجد نتائج"}
                          className="mt-2"
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '40px',
                              borderColor: '#e2e8f0',
                              '&:hover': {
                                borderColor: '#cbd5e1'
                              }
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 9999
                            })
                          }}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm">السعر</Label>
                          <Input
                            data-testid="item-price-input"
                            type="number"
                            placeholder="0.00"
                            value={currentItem.price}
                            onChange={(e) => setCurrentItem({...currentItem, price: e.target.value})}
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">الكمية</Label>
                          <Input
                            data-testid="item-quantity-input"
                            type="number"
                            placeholder="0"
                            value={currentItem.quantity}
                            onChange={(e) => setCurrentItem({...currentItem, quantity: e.target.value})}
                            className="mt-2"
                          />
                        </div>
                      </div>

                      <Button 
                        data-testid="add-item-to-list-btn"
                        onClick={handleAddItem}
                        type="button"
                        variant="outline"
                        className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <Plus className="w-4 h-4 ml-2" />
                        إضافة الصنف للقائمة
                      </Button>
                    </div>

                    {selectedItems.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <Label className="text-sm font-semibold">الأصناف المضافة ({selectedItems.length}):</Label>
                        {selectedItems.map((item, index) => (
                          <div 
                            key={index} 
                            className="p-3 bg-purple-50 rounded-lg space-y-2"
                            data-testid={`selected-item-${index}`}
                          >
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-slate-800">{item.item_name}</p>
                              <Button
                                data-testid={`remove-item-${index}`}
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveItem(index)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">السعر</Label>
                                <Input
                                  type="number"
                                  value={item.price}
                                  onChange={(e) => handleUpdateItem(index, 'price', e.target.value)}
                                  placeholder="0.00"
                                  className="mt-1"
                                  step="0.01"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">الكمية</Label>
                                <Input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => handleUpdateItem(index, 'quantity', e.target.value)}
                                  placeholder="0"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>صورة العرض</Label>
                    <div 
                      {...getRootProps()} 
                      data-testid="image-dropzone"
                      className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                        isDragActive ? 'border-purple-500 bg-purple-50' : 'border-slate-300 hover:border-purple-400'
                      }`}
                    >
                      <input {...getInputProps()} />
                      {imagePreview ? (
                        <div className="space-y-2">
                          <img src={imagePreview} alt="Preview" className="max-h-48 mx-auto rounded" />
                          <p className="text-sm text-slate-600">انقر أو اسحب لتغيير الصورة</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <ImageIcon className="w-12 h-12 mx-auto text-slate-400" />
                          <p className="text-slate-600">اسحب الصورة هنا أو انقر للاختيار</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>ملاحظات (اختياري)</Label>
                    <Textarea
                      data-testid="notes-input"
                      placeholder="أي ملاحظات إضافية"
                      value={newQuote.notes}
                      onChange={(e) => setNewQuote({...newQuote, notes: e.target.value})}
                      className="mt-2"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      data-testid="submit-quote-btn"
                      onClick={handleAdd} 
                      disabled={loading || selectedItems.length === 0}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 hover:shadow-lg"
                    >
                      {editingQuote ? 'تحديث عرض السعر' : 'حفظ عرض السعر'} ({selectedItems.length} صنف)
                    </Button>
                    
                    {hasUnsavedChanges && (
                      <Button 
                        onClick={() => {
                          localStorage.removeItem('quote_draft');
                          setHasUnsavedChanges(false);
                          toast.success('تم حذف المسودة المحفوظة');
                        }}
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        حذف المسودة
                      </Button>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Back button - Desktop only */}
            <Button 
              data-testid="back-to-dashboard-btn-desktop"
              onClick={() => navigate('/procurement')} 
              variant="outline"
              className="gap-2 min-w-[120px] shrink-0 hidden md:flex"
            >
              <ArrowLeft className="w-5 h-5 shrink-0" />
              <span>العودة</span>
            </Button>
          </div>
        </div>

        <Card className="border-0 shadow-xl backdrop-blur-sm bg-white/90">
          <CardHeader>
            <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>قائمة عروض الأسعار</CardTitle>
          </CardHeader>
          <CardContent>
            {quotes.length === 0 ? (
              <p className="text-center text-slate-500 py-8">لا توجد عروض أسعار. قم بإضافة عرض جديد</p>
            ) : (
              <>
              {showFilters && (
                <Card className="mb-6 border-2 border-blue-200 bg-blue-50/50 animate-in slide-in-from-top duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">تصفية عروض الأسعار</CardTitle>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={clearFilters}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          مسح الفلاتر
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowFilters(false)}
                          className="text-slate-600 hover:text-slate-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {/* Project Filter */}
                    <div>
                      <Label className="text-sm mb-2 block">المشروع</Label>
                      <Select value={filters.project_id} onValueChange={(value) => setFilters({...filters, project_id: value === 'all' ? '' : value})}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="كل المشاريع" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">كل المشاريع</SelectItem>
                          {projects.map(project => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.project_number} - {project.project_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Supplier Filter */}
                    <div>
                      <Label className="text-sm mb-2 block">المورد</Label>
                      <Select value={filters.supplier_name} onValueChange={(value) => setFilters({...filters, supplier_name: value === 'all' ? '' : value})}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="كل الموردين" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">كل الموردين</SelectItem>
                          {uniqueSuppliers.map(supplier => (
                            <SelectItem key={supplier} value={supplier}>
                              {supplier}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Tag Filter */}
                    <div>
                      <Label className="text-sm mb-2 block">تصنيف العرض</Label>
                      <Select value={filters.tag_id} onValueChange={(value) => setFilters({...filters, tag_id: value === 'all' ? '' : value})}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="كل التصنيفات" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">كل التصنيفات</SelectItem>
                          {tags.map(tag => (
                            <SelectItem key={tag.id} value={tag.id}>
                              {tag.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Main Category Filter */}
                    <div>
                      <Label className="text-sm mb-2 block">التصنيف الرئيسي</Label>
                      <Select value={filters.main_category_id} onValueChange={(value) => setFilters({...filters, main_category_id: value === 'all' ? '' : value, sub_category_id: ''})}>
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="كل التصنيفات" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">كل التصنيفات</SelectItem>
                          {mainCategories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Sub Category Filter */}
                    <div>
                      <Label className="text-sm mb-2 block">التصنيف الفرعي</Label>
                      <Select 
                        value={filters.sub_category_id} 
                        onValueChange={(value) => setFilters({...filters, sub_category_id: value === 'all' ? '' : value})}
                        disabled={!filters.main_category_id}
                      >
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="كل الفروع" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">كل الفروع</SelectItem>
                          {subCategories
                            .filter(sub => !filters.main_category_id || sub.main_category_id === filters.main_category_id)
                            .map(sub => (
                              <SelectItem key={sub.id} value={sub.id}>
                                {sub.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Active Filters Count */}
                  <div className="mt-4 text-sm text-slate-600">
                    عرض <span className="font-bold text-blue-600">{filteredQuotes.length}</span> من {quotes.length} عرض سعر
                  </div>
                  </CardContent>
                </Card>
              )}

              {viewMode === 'table' ? (
                // Table View - Compact
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
                        <th className="p-3 text-right text-sm font-semibold border">رقم العرض</th>
                        <th className="p-3 text-right text-sm font-semibold border">المورد</th>
                        <th className="p-3 text-right text-sm font-semibold border">المشروع</th>
                        <th className="p-3 text-right text-sm font-semibold border">التاريخ</th>
                        <th className="p-3 text-right text-sm font-semibold border">الأصناف</th>
                        <th className="p-3 text-right text-sm font-semibold border">الإجمالي</th>
                        <th className="p-3 text-center text-sm font-semibold border">الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredQuotes.map((quote, index) => {
                        const hasMultipleItems = quote.items && quote.items.length > 0;
                        const displayItems = hasMultipleItems ? quote.items : [{
                          item_name: quote.item_name,
                          price: quote.price,
                          quantity: quote.quantity
                        }];
                        const totalAmount = displayItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                        
                        return (
                          <tr key={quote.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-purple-50 transition-colors`}>
                            <td className="p-3 border">
                              <span className="font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded text-sm">
                                {quote.quote_number || '-'}
                              </span>
                            </td>
                            <td className="p-3 border">
                              <div className="font-semibold text-slate-800">{quote.supplier_name}</div>
                              {quote.tag_name && (
                                <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded-md inline-block mt-1">{quote.tag_name}</span>
                              )}
                            </td>
                            <td className="p-3 border text-sm text-slate-700">{quote.project_number || '-'}</td>
                            <td className="p-3 border text-sm text-slate-700">{quote.quote_date}</td>
                            <td className="p-3 border">
                              <div className="text-sm text-slate-700">
                                {displayItems.length === 1 ? (
                                  displayItems[0].item_name
                                ) : (
                                  <details className="cursor-pointer">
                                    <summary className="font-semibold text-purple-600">{displayItems.length} صنف</summary>
                                    <ul className="mt-2 space-y-1 pr-4">
                                      {displayItems.map((item, idx) => (
                                        <li key={idx} className="text-xs">• {item.item_name}</li>
                                      ))}
                                    </ul>
                                  </details>
                                )}
                              </div>
                            </td>
                            <td className="p-3 border">
                              <span className="font-bold text-purple-700">{totalAmount.toFixed(2)} ريال</span>
                            </td>
                            <td className="p-3 border">
                              <div className="flex justify-center gap-1">
                                {quote.image_url && (
                                  <a href={`${process.env.REACT_APP_BACKEND_URL}${quote.image_url}`} target="_blank" rel="noopener noreferrer">
                                    <Button variant="ghost" size="sm" className="text-purple-600 h-8 w-8 p-0" title="صورة">
                                      <ImageIcon className="w-4 h-4" />
                                    </Button>
                                  </a>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => handlePrint(quote)} className="text-green-600 h-8 w-8 p-0" title="طباعة">
                                  <Printer className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleShare(quote)} className="text-orange-600 h-8 w-8 p-0" title="مشاركة">
                                  <Share2 className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(quote)} className="text-blue-600 h-8 w-8 p-0" title="تعديل">
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDuplicate(quote)} className="text-purple-600 h-8 w-8 p-0" title="نسخ">
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(quote.id)} className="text-red-600 h-8 w-8 p-0" title="حذف">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Cards View - Compact New Design
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredQuotes.map((quote) => {
                    const hasMultipleItems = quote.items && quote.items.length > 0;
                    const displayItems = hasMultipleItems ? quote.items : [{
                      item_name: quote.item_name,
                      main_category_name: quote.main_category_name || quote.category_name,
                      price: quote.price,
                      quantity: quote.quantity
                    }];
                    
                    const totalAmount = displayItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

                    return (
                      <Card key={quote.id} data-testid={`quote-item-${quote.id}`} className="border-2 border-slate-200 hover:border-purple-300 transition-all hover:shadow-lg">
                        <CardContent className="p-6">
                          {/* Quote Number & Date at top */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {quote.quote_number && (
                                <span className="text-lg font-bold text-purple-700 bg-purple-50 px-3 py-1 rounded-lg">
                                  {quote.quote_number}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span>{quote.quote_date}</span>
                            </div>
                          </div>
                          
                          {/* Project info */}
                          {quote.project_number && (
                            <div className="text-xs text-slate-500 mb-3">
                              المشروع: {quote.project_number}
                            </div>
                          )}
                          
                          {/* Supplier Name */}
                          <div className="mb-4">
                            <h3 className="text-2xl font-bold text-slate-800 mb-1">{quote.supplier_name}</h3>
                            {quote.tag_name && (
                              <span className="text-xs bg-pink-100 text-pink-700 px-3 py-1 rounded-full inline-block">{quote.tag_name}</span>
                            )}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            {quote.image_url && (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${quote.image_url}`} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="text-purple-600 border-purple-200 hover:bg-purple-50">
                                  <ImageIcon className="w-4 h-4 mr-2" />
                                  <span className="hidden sm:inline">صورة</span>
                                </Button>
                              </a>
                            )}
                            <Button
                              data-testid={`print-quote-${quote.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => handlePrint(quote)}
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              title="طباعة"
                            >
                              <Printer className="w-4 h-4 mr-2" />
                              <span className="hidden sm:inline">طباعة</span>
                            </Button>
                            <Button
                              data-testid={`share-quote-${quote.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleShare(quote)}
                              className="text-orange-600 border-orange-200 hover:bg-orange-50"
                              title="مشاركة"
                            >
                              <Share2 className="w-4 h-4 mr-2" />
                              <span className="hidden sm:inline">مشاركة</span>
                            </Button>
                            <Button
                              data-testid={`edit-quote-${quote.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(quote)}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              title="تعديل"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              <span className="hidden sm:inline">تعديل</span>
                            </Button>
                            <Button
                              data-testid={`duplicate-quote-${quote.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleDuplicate(quote)}
                              className="text-purple-600 border-purple-200 hover:bg-purple-50"
                              title="نسخ"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              <span className="hidden sm:inline">نسخ</span>
                            </Button>
                            <Button
                              data-testid={`delete-quote-${quote.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(quote.id)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              <span className="hidden sm:inline">حذف</span>
                            </Button>
                          </div>
                          
                          {/* Total at bottom */}
                          <div className="border-t-2 border-slate-200 pt-4">
                            <div className="flex items-center justify-between">
                              <span className="text-lg font-semibold text-slate-600">الإجمالي</span>
                              <span className="text-2xl font-bold text-purple-700">{totalAmount.toFixed(2)} ر.س</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1 text-left">
                              {displayItems.length} {displayItems.length === 1 ? 'صنف' : 'أصناف'}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Quick Add Dialogs */}
      
      {/* Add Project Dialog */}
      <Dialog open={quickAddDialogs.project} onOpenChange={(open) => setQuickAddDialogs({...quickAddDialogs, project: open})}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>إضافة مشروع جديد</DialogTitle>
            <DialogDescription>أدخل بيانات المشروع الجديد</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>رقم المشروع *</Label>
              <Input
                value={newProject.project_number}
                onChange={(e) => setNewProject({...newProject, project_number: e.target.value})}
                placeholder="PRJ-001"
                className="mt-2"
              />
            </div>
            <div>
              <Label>اسم المشروع *</Label>
              <Input
                value={newProject.project_name}
                onChange={(e) => setNewProject({...newProject, project_name: e.target.value})}
                placeholder="اسم المشروع"
                className="mt-2"
              />
            </div>
            <div>
              <Label>الوصف</Label>
              <Input
                value={newProject.description}
                onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                placeholder="وصف المشروع"
                className="mt-2"
              />
            </div>
            <div>
              <Label>الموقع</Label>
              <Input
                value={newProject.location}
                onChange={(e) => setNewProject({...newProject, location: e.target.value})}
                placeholder="موقع المشروع"
                className="mt-2"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <Button onClick={handleQuickAddProject} className="flex-1">
              حفظ
            </Button>
            <Button variant="outline" onClick={() => setQuickAddDialogs({...quickAddDialogs, project: false})} className="flex-1">
              إلغاء
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add Supplier Dialog */}
      <Dialog open={quickAddDialogs.supplier} onOpenChange={(open) => setQuickAddDialogs({...quickAddDialogs, supplier: open})}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>إضافة مورد جديد</DialogTitle>
            <DialogDescription>أدخل بيانات المورد الجديد</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>اسم المورد *</Label>
              <Input
                value={newSupplier.name}
                onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})}
                placeholder="اسم المورد"
                className="mt-2"
              />
            </div>
            <div>
              <Label>معلومات الاتصال</Label>
              <Input
                value={newSupplier.contact_info}
                onChange={(e) => setNewSupplier({...newSupplier, contact_info: e.target.value})}
                placeholder="رقم الهاتف أو البريد الإلكتروني"
                className="mt-2"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <Button onClick={handleQuickAddSupplier} className="flex-1">
              حفظ
            </Button>
            <Button variant="outline" onClick={() => setQuickAddDialogs({...quickAddDialogs, supplier: false})} className="flex-1">
              إلغاء
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add Tag Dialog */}
      <Dialog open={quickAddDialogs.tag} onOpenChange={(open) => setQuickAddDialogs({...quickAddDialogs, tag: open})}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>إضافة تصنيف جديد</DialogTitle>
            <DialogDescription>أدخل اسم التصنيف الجديد</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>اسم التصنيف *</Label>
              <Input
                value={newTag.name}
                onChange={(e) => setNewTag({...newTag, name: e.target.value})}
                placeholder="اسم التصنيف"
                className="mt-2"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <Button onClick={handleQuickAddTag} className="flex-1">
              حفظ
            </Button>
            <Button variant="outline" onClick={() => setQuickAddDialogs({...quickAddDialogs, tag: false})} className="flex-1">
              إلغاء
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add Item Dialog */}
      <Dialog open={quickAddDialogs.item} onOpenChange={(open) => setQuickAddDialogs({...quickAddDialogs, item: open})}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>إضافة صنف جديد</DialogTitle>
            <DialogDescription>أدخل بيانات الصنف الجديد</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>اسم الصنف *</Label>
              <Input
                value={newItem.name}
                onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                placeholder="اسم الصنف"
                className="mt-2"
              />
            </div>
            <div>
              <Label>التصنيف الرئيسي *</Label>
              <Select 
                value={newItem.main_category_id} 
                onValueChange={(value) => setNewItem({...newItem, main_category_id: value, sub_category_id: ''})}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="اختر التصنيف الرئيسي" />
                </SelectTrigger>
                <SelectContent position="item-aligned">
                  {mainCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>التصنيف الفرعي *</Label>
              <Select 
                value={newItem.sub_category_id} 
                onValueChange={(value) => setNewItem({...newItem, sub_category_id: value})}
                disabled={!newItem.main_category_id}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="اختر التصنيف الفرعي" />
                </SelectTrigger>
                <SelectContent position="item-aligned">
                  {subCategories
                    .filter(cat => cat.main_category_id === newItem.main_category_id)
                    .map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>الوصف</Label>
              <Textarea
                value={newItem.description}
                onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                placeholder="وصف الصنف"
                className="mt-2"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <Button onClick={handleQuickAddItem} className="flex-1">
              حفظ
            </Button>
            <Button variant="outline" onClick={() => setQuickAddDialogs({...quickAddDialogs, item: false})} className="flex-1">
              إلغاء
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
